import React from "react";
import { RouteObject, redirect } from "react-router";
import { ProductRoutes } from "src/router/routes/Products";
import { IntegrationRoutes } from "src/router/routes/orgs/Integrations";
import { DocumentRoutes } from "src/router/routes/orgs/Documents";
import { UserRoutes } from "src/router/routes/orgs/Users";
import { FinancingRoutes } from "src/router/routes/orgs/Financing";
import { Redirect } from "src/router/helper/Redirect";

export const reloadOrgValues = ["info", "settings", "estimator", "sharing", "documents", "products"];

// '/orgs/*'
export const OrgRoutes: RouteObject[] = [
  {
    path: ":orgId",
    async lazy() {
      const { Configuration } = await import(/* webpackChunkName: "orgs-cache-configuration" */ "orgs/Configuration");
      return { element: <Configuration /> };
    },
    shouldRevalidate: ({ currentUrl, nextUrl, currentParams, nextParams }) => {
      const currentUrlOrgId = currentParams.orgId;
      const nextUrlOrgId = nextParams.orgId;

      return reloadOrgValues.some(
        (value) =>
          nextUrl.pathname.includes(value) &&
          (!currentUrl.pathname.includes(value) || currentUrlOrgId !== nextUrlOrgId),
      );
    },
    async loader(args) {
      const { orgLoader } = await import(/* webpackChunkName: "orgs-cache-loader" */ "src/router/loaders/Orgs");
      return orgLoader(args);
    },
    children: [
      {
        path: "settings",
        async loader(args) {
          const { globalSettingsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-settings-loader" */ "src/router/loaders/Orgs"
          );
          return globalSettingsTabLoader(args);
        },
        async lazy() {
          const { GlobalSettingsTab } = await import(
            /* webpackChunkName: "orgs-cache-settings" */ "orgs/Configuration/GlobalSettingsTab"
          );
          return { element: <GlobalSettingsTab /> };
        },
        children: [
          {
            path: "job_resulting/:optionIndex/:fieldIndex",
            async lazy() {
              const { EditFieldModal } = await import(
                /* webpackChunkName: "orgs-cache-job-resulting-add-field-modal" */ "orgs/Configuration/GlobalSettingsTab/JobResulting/EditFieldModal"
              );
              return { element: <EditFieldModal /> };
            },
          },
        ],
      },
      {
        path: "info",
        async loader(args) {
          const { infoTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-info-loader" */ "src/router/loaders/Orgs"
          );
          return infoTabLoader(args);
        },
        children: [
          {
            path: "duplicate",
            async loader(args) {
              const { duplicateOrgsLoader } = await import(
                /* webpackChunkName: "orgs-cache-duplicate-loader" */ "src/router/loaders/Orgs"
              );
              return duplicateOrgsLoader(args);
            },
            async lazy() {
              const { DuplicateData } = await import(
                /* webpackChunkName: "orgs-cache-duplicate" */ "orgs/Configuration/InfoTab/DuplicateData"
              );
              return { element: <DuplicateData /> };
            },
          },
          {
            path: "",
            async lazy() {
              const { InfoTab } = await import(/* webpackChunkName: "orgs-cache-info" */ "orgs/Configuration/InfoTab");
              return { element: <InfoTab /> };
            },
            children: [
              {
                path: "billing",
                children: [
                  {
                    path: ":customerId",
                    async lazy() {
                      const { MaxioModal } = await import(
                        /* webpackChunkName: "orgs-cache-maxio-modal" */ "orgs/Configuration/InfoTab/MaxioModal/index"
                      );
                      return { element: <MaxioModal /> };
                    },
                    async loader(args) {
                      const { billingLoader } = await import(
                        /* webpackChunkName: "orgs-cache-billing-loader" */ "src/router/loaders/Orgs"
                      );
                      return billingLoader(args);
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "child_orgs",
        async loader(args) {
          const { childOrgsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-child-orgs-loader" */ "src/router/loaders/Orgs"
          );
          return childOrgsTabLoader(args);
        },
        async lazy() {
          const { ChildOrganizationsTab } = await import(
            /* webpackChunkName: "orgs-cache-child-orgs" */ "orgs/Configuration/ChildOrganizationsTab"
          );
          return { element: <ChildOrganizationsTab /> };
        },
      },
      {
        path: "jobs",
        async loader(args) {
          const { jobsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-jobs-loader" */ "src/router/loaders/Orgs"
          );
          return jobsTabLoader(args);
        },
        async lazy() {
          const { JobsTab } = await import(
            /* webpackChunkName: "orgs-cache-jobs" */ "orgs/Configuration/JobsTab/index"
          );
          return { element: <JobsTab /> };
        },
        children: [
          {
            path: "filters",
            async loader(args) {
              const { jobsTabFiltersLoader } = await import("src/router/loaders/Orgs");
              return jobsTabFiltersLoader(args);
            },
            async lazy() {
              const { Filters } = await import("jobs/List/Filters");
              return { element: <Filters jobParamsType="orgJobParams" /> };
            },
          },
        ],
      },
      {
        path: "analytics",
        async loader(args) {
          const { analyticsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-analytics-loader" */ "src/router/loaders/Orgs"
          );
          return analyticsTabLoader(args);
        },
        async lazy() {
          const { AnalyticsTab } = await import(
            /* webpackChunkName: "orgs-cache-analytics" */ "orgs/Configuration/AnalyticsTab"
          );
          return { element: <AnalyticsTab /> };
        },
      },
      {
        path: "estimator",
        async loader(args) {
          const { estimatorTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-estimator-loader" */ "src/router/loaders/Orgs"
          );
          return estimatorTabLoader(args);
        },
        async lazy() {
          const { EstimatorTab } = await import(
            /* webpackChunkName: "orgs-cache-estimator" */ "orgs/Configuration/EstimatorTab"
          );
          return { element: <EstimatorTab /> };
        },
      },
      {
        path: "measurements",
        async loader(args) {
          const { measurementsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-measurements-loader" */ "src/router/loaders/Orgs"
          );
          return measurementsTabLoader(args);
        },
        async lazy() {
          const { MeasurementsTab } = await import(
            /* webpackChunkName: "orgs-cache-measurements" */ "orgs/Configuration/MeasurementsTab"
          );
          return { element: <MeasurementsTab /> };
        },
      },
      {
        path: "documents",
        async loader(args) {
          const { documentsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-documents-loader" */ "src/router/loaders/Orgs"
          );
          return documentsTabLoader(args);
        },
        async lazy() {
          const { DocumentsTab } = await import(
            /* webpackChunkName: "orgs-cache-documents" */ "orgs/Configuration/DocumentsTab"
          );
          return { element: <DocumentsTab /> };
        },
        children: DocumentRoutes,
      },
      {
        path: "presentations",
        async loader(args) {
          const { presentationsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-presentations-loader" */ "src/router/loaders/Orgs"
          );
          return presentationsTabLoader(args);
        },
        children: [
          {
            path: ":presentationId/present",
            async lazy() {
              const { ShowPresentation } = await import(
                /* webpackChunkName: "orgs-cache-show-presentation" */ "common/components/ShowPresentation"
              );
              return { element: <ShowPresentation analyticsData={{ category: "OrgPresentations" }} /> };
            },
            async loader(args) {
              const { presentationPresentLoader } = await import(
                /* webpackChunkName: "orgs-cache-presentation-present-loader" */ "src/router/loaders/Orgs"
              );
              return presentationPresentLoader(args);
            },
          },
          {
            path: "",
            async loader(args) {
              const { presentationsFolderLoader } = await import(
                /* webpackChunkName: "orgs-cache-presentations-folder-loader" */ "src/router/loaders/Orgs"
              );
              return presentationsFolderLoader(args);
            },
            async lazy() {
              const { PresentationsTab } = await import(
                /* webpackChunkName: "orgs-cache-presentations" */ "orgs/Configuration/PresentationsTab"
              );
              return { element: <PresentationsTab /> };
            },
          },
        ],
      },
      {
        path: "financing",
        async loader(args) {
          const { financingTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-financing-loader" */ "src/router/loaders/Orgs"
          );
          return financingTabLoader(args);
        },
        async lazy() {
          const { FinancingTab } = await import(
            /* webpackChunkName: "orgs-cache-financing" */ "orgs/Configuration/FinancingTab"
          );
          return { element: <FinancingTab /> };
        },
        children: FinancingRoutes,
      },
      {
        path: "tools",
        async loader(args) {
          const { toolsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-tools-loader" */ "src/router/loaders/Orgs"
          );
          return toolsTabLoader(args);
        },
        async lazy() {
          const { ToolsTab } = await import(/* webpackChunkName: "orgs-cache-tools" */ "orgs/Configuration/ToolsTab");
          return { element: <ToolsTab /> };
        },
      },
      {
        path: "sharing",
        async loader(args) {
          const { sharingPreferencesTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-sharing-loader" */ "src/router/loaders/Orgs"
          );
          return sharingPreferencesTabLoader(args);
        },
        async lazy() {
          const { SharingPreferencesTab } = await import(
            /* webpackChunkName: "orgs-cache-sharing" */ "orgs/Configuration/SharingPreferencesTab"
          );
          return { element: <SharingPreferencesTab /> };
        },
      },
      {
        path: "users",
        async loader(args) {
          const { usersTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-users-loader" */ "src/router/loaders/Orgs"
          );
          return usersTabLoader(args);
        },
        children: UserRoutes,
      },
      {
        path: "job_statuses",
        async loader(args) {
          const { jobStatusesTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-job-statuses-loader" */ "src/router/loaders/Orgs"
          );
          return jobStatusesTabLoader(args);
        },
      },
      {
        path: "access_management",
        async loader(args) {
          const { accessManagementTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-access-management-loader" */ "src/router/loaders/Orgs"
          );
          return accessManagementTabLoader(args);
        },
        async lazy() {
          const { AccessManagementTab } = await import(
            /* webpackChunkName: "orgs-cache-access-management" */ "orgs/Configuration/AccessManagementTab"
          );
          return { element: <AccessManagementTab /> };
        },
      },
      {
        path: "integrations",
        async loader(args) {
          const { integrationsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-integrations-loader" */ "src/router/loaders/Orgs"
          );
          return integrationsTabLoader(args);
        },
        shouldRevalidate: () => false,
        children: IntegrationRoutes,
      },
      {
        path: "products",
        async loader(args) {
          const { productsTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-products-loader" */ "src/router/loaders/Orgs"
          );
          return productsTabLoader(args);
        },
        children: ProductRoutes,
      },
      {
        path: "webhooks",
        async loader(args) {
          const { webhooksTabLoader } = await import(
            /* webpackChunkName: "orgs-cache-webhooks-loader" */ "src/router/loaders/Orgs"
          );
          return webhooksTabLoader(args);
        },
        async lazy() {
          const { WebhooksTab } = await import(
            /* webpackChunkName: "orgs-cache-webhooks" */ "orgs/Configuration/WebhooksTab"
          );
          return { element: <WebhooksTab /> };
        },
      },
      {
        path: "webhooks/:webhookId",
        async loader(args) {
          const { webhookEventsLoader } = await import(
            /* webpackChunkName: "orgs-cache-webhook-events-loader" */ "src/router/loaders/Orgs"
          );
          return webhookEventsLoader(args);
        },
        async lazy() {
          const { EditWebhook } = await import(
            /* webpackChunkName: "orgs-cache-edit-webhook" */ "orgs/Configuration/WebhooksTab/EditWebhook"
          );
          return { element: <EditWebhook /> };
        },
      },
      {
        path: "",
        loader: () => {
          return redirect("info");
        },
      },
      {
        path: "*",
        loader: () => {
          return redirect("/orgs");
        },
      },
    ],
  },
  {
    path: "accesses",
    async lazy() {
      const { Accesses } = await import(/* webpackChunkName: "orgs-cache-accesses" */ "orgs/Accesses");
      return { element: <Accesses /> };
    },
    async loader(args) {
      const { accessesLoader } = await import(
        /* webpackChunkName: "orgs-cache-accesses-loader" */ "src/router/loaders/Orgs"
      );
      return accessesLoader(args);
    },
  },
  {
    path: "",
    async loader(args) {
      const { orgsLoader } = await import(/* webpackChunkName: "orgs-cache-loader" */ "src/router/loaders/Orgs");
      return orgsLoader(args);
    },
    async lazy() {
      const { List } = await import(/* webpackChunkName: "orgs-cache-list" */ "orgs/List");
      return { element: <List /> };
    },
  },
  {
    path: "*",
    element: <Redirect to="/orgs" />,
  },
];
